import { useEffect, useState } from "react";
import { ChevronRight, Search, Bell, Plus, Menu } from "lucide-react";
import { FontSizeButton } from "./FontSizeButton";

export type BreadcrumbItem = { label: string; href?: string };

interface TopBarProps {
  breadcrumb?: BreadcrumbItem[];
  onToggleSidebar?: () => void;
  onOpenSearch?: () => void;
}

const iconBtn =
  "flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)] hover:text-foreground";

export function TopBar({
  breadcrumb = [],
  onToggleSidebar,
  onOpenSearch,
}: TopBarProps) {
  // Plus is reserved for a future Quick Create flow — for now it shows a
  // self-dismissing "coming soon" notice (no toast library in the app).
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  useEffect(() => {
    if (!showQuickCreate) return;
    const t = window.setTimeout(() => setShowQuickCreate(false), 2500);
    return () => window.clearTimeout(t);
  }, [showQuickCreate]);

  return (
    <header
      className="relative z-50 flex h-[46px] shrink-0 items-center justify-between border-b bg-white px-4"
      style={{ borderColor: "hsl(210,30%,85%)" }}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          aria-label="Toggle sidebar"
        >
          <Menu className="h-4 w-4" strokeWidth={1.5} />
        </button>
        {breadcrumb.length > 0 && (
          <nav className="flex items-center gap-1 text-[12px] text-muted-foreground">
            {breadcrumb.map((item, i) => {
              const last = i === breadcrumb.length - 1;
              return (
                <span key={`${item.label}-${i}`} className="flex items-center gap-1">
                  {item.href && !last ? (
                    <a href={item.href} className="hover:text-foreground">
                      {item.label}
                    </a>
                  ) : (
                    <span className={last ? "text-foreground" : ""}>{item.label}</span>
                  )}
                  {!last && <ChevronRight className="h-3 w-3" strokeWidth={1.5} />}
                </span>
              );
            })}
          </nav>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onOpenSearch}
          className={iconBtn}
          title="Search (Ctrl/Cmd + K)"
          aria-label="Open global search"
        >
          <Search className="h-4 w-4" strokeWidth={1.5} />
        </button>

        <FontSizeButton />

        <button
          type="button"
          className={iconBtn}
          title="Notifications"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" strokeWidth={1.5} />
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowQuickCreate((v) => !v)}
            className={iconBtn}
            title="Quick Create"
            aria-label="Quick Create"
          >
            <Plus className="h-4 w-4" strokeWidth={1.5} />
          </button>
          {showQuickCreate && (
            <div
              className="absolute right-0 top-[calc(100%+8px)] z-[9999] w-56 rounded-lg bg-white p-3 text-xs"
              style={{
                border: "1px solid hsl(210,30%,85%)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              }}
              role="status"
            >
              <p className="font-semibold text-foreground">Quick Create</p>
              <p className="mt-0.5 text-muted-foreground">
                Coming soon — create records from anywhere.
              </p>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
